import { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  Calendar,
  Clock,
  DollarSign,
  FileText,
  List,
  RefreshCw,
  User,
} from "lucide-react";
import {
  BookingStatusType,
  InventorySchema,
  ResourceBookingSchema,
  ResourceSchema,
} from "../data/types";
import { useLoadMember, useLoadResourceBookings } from "../hooks/memberhooks";
import UserAvatar from "../components/UserAvatar";
import moment from "moment-timezone";
import {
  getBookingInventoryQuantity,
  getEffectiveInventoryAvailableQuantity,
  getInventoryBookingsForItem,
  getInventoryStockProgressFillClassForItem,
  getInventoryStockStatusDot,
  getInventoryStockStatusLabel,
  getInventoryUsedPercentForItem,
  getInventoryUsedQuantityForItem,
  inventoryStockStatusDotClass,
  type InventoryStockVisualStatus,
} from "../utils/helpers/inventoryStockStatus";
import { cn } from "../utils/utils";
import { getBookingStatusColor, styleThousandCommas } from "../utils/helpers";
import { RequestDetailsModal } from "./AppModal";

type InventoryDetailTab = "overview" | "sessions" | "usage" | "audit";

const TABS: { id: InventoryDetailTab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "sessions", label: "Booking sessions" },
  { id: "usage", label: "Admin log" },
  { id: "audit", label: "Audit trail" },
];

const USAGE_STATUSES: readonly BookingStatusType[] = ["approved", "in_progress", "completed"];

const DEFAULT_TIMEZONE = "America/New_York";

type InventoryItemDetailsProps = {
  resourceDetails: ResourceSchema;
  inventory?: InventorySchema | null;
};

type OverviewRowProps = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  progress?: number;
  progressTrackClass?: string;
  progressFillClass?: string;
  progressTone?: "success" | "muted";
  statusDot?: InventoryStockVisualStatus;
};

/** Display label for a booking status in inventory session lists. */
const getSessionStatusLabel = (status?: BookingStatusType | string): string => {
  switch (String(status || "").toLowerCase()) {
    case "pending":
      return "Pending";
    case "approved":
      return "Approved";
    case "canceled":
      return "Canceled";
    case "in_progress":
      return "In Progress";
    case "completed":
      return "Completed";
    default:
      return status ? String(status) : "Unknown";
  }
};

/** Format a booking timestamp in the booking timezone. */
const formatSessionTime = (value?: string, timezone?: string): string => {
  if (!value) return "N/A";
  const tz = timezone || DEFAULT_TIMEZONE;
  const parsed = moment(value).tz(tz);
  return parsed.isValid() ? parsed.format("ddd MMM DD, h:mm A") : "N/A";
};

/** Member / business display name on a booking. */
const getBookingPartyName = (booking: ResourceBookingSchema): string =>
  booking.user_full_name?.trim()
  || booking.business_name?.trim()
  || booking.username?.trim()
  || "Unknown member";

const OverviewRow = ({
  icon: Icon,
  label,
  value,
  progress,
  progressTrackClass,
  progressFillClass,
  progressTone = "muted",
  statusDot,
}: OverviewRowProps) => (
  <div className="flex items-start gap-3 border-b border-inventory-border py-3 last:border-b-0">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-inventory-primary/35" />
    <div className="min-w-0 flex-1">
      <p className="font-open-sans text-sm text-inventory-primary/50">{label}</p>
      {progress !== undefined ? (
        <div className="mt-2">
          <div
            className={cn(
              "h-2 overflow-hidden rounded-full",
              progressTrackClass ?? "bg-[#f0f0f0]"
            )}
          >
            <div
              className={cn(
                "h-full rounded-full",
                progressFillClass ??
                  (progressTone === "success" ? "bg-[#128c44]" : "bg-[#e0e0e0]")
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-1.5 font-open-sans text-sm text-inventory-primary">{value}</p>
        </div>
      ) : (
        <div className="mt-0.5 flex items-center gap-2 font-open-sans text-sm text-inventory-primary">
          {statusDot && (
            <span
              className={cn(
                "inline-block h-2 w-2 shrink-0 rounded-full",
                inventoryStockStatusDotClass[statusDot]
              )}
            />
          )}
          {value}
        </div>
      )}
    </div>
  </div>
);

const EmptyTabPanel = ({ message }: { message: string }) => (
  <div className="rounded-xl border border-inventory-border bg-inventory-surface-muted/30 px-4 py-8 text-center">
    <p className="font-open-sans text-sm text-inventory-primary/55">{message}</p>
  </div>
);

const SessionStatusChip = ({ status }: { status?: BookingStatusType }) => {
  const colors = getBookingStatusColor(status || "");
  return (
    <span
      className="rounded-full px-2 py-0.5 font-raleway text-[10px] font-bold uppercase tracking-wide"
      style={{ color: colors.primaryTextColor, backgroundColor: colors.bgColor }}
    >
      {getSessionStatusLabel(status)}
    </span>
  );
};

export const InventoryItemDetails = ({ inventory, resourceDetails }: InventoryItemDetailsProps) => {
  const [tab, setTab] = useState<InventoryDetailTab>("overview");
  const [viewRequestDetails, toggleViewRequestDetails] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ResourceBookingSchema | null>(null);

  const inventoryId = inventory?.id || "";
  const resourceId = resourceDetails.id || "";
  const { resourceBookings } = useLoadResourceBookings(resourceId);

  /** Opens RequestDetailsModal for a booking session row, matching ResourceRequestRecord. */
  const handleOpenRequestDetails = (booking: ResourceBookingSchema) => {
    setSelectedRequest(booking);
    toggleViewRequestDetails(true);
  };

  const matchingBookings = useMemo(
    () => getInventoryBookingsForItem(resourceBookings, inventoryId),
    [resourceBookings, inventoryId]
  );

  const sortedSessions = useMemo(
    () =>
      [...matchingBookings].sort(
        (a, b) => new Date(b.start_time || "").getTime() - new Date(a.start_time || "").getTime()
      ),
    [matchingBookings]
  );

  const latestResourceBookingWithInventoryId = sortedSessions[0];

  const totalQty = inventory?.total_quantity || 0;
  const stockContext = {
    total_quantity: totalQty,
    available_quantity: inventory?.available_quantity,
    is_reusable: inventory?.is_reusable,
    inventoryId,
    bookings: resourceBookings,
  };
  const availableQty = getEffectiveInventoryAvailableQuantity(stockContext);
  const usedQty = getInventoryUsedQuantityForItem(stockContext);
  const usedPct = getInventoryUsedPercentForItem(stockContext);
  const stockStatusLabel = getInventoryStockStatusLabel(stockContext);
  const stockStatusDot = getInventoryStockStatusDot(stockContext);
  const stockProgressFillClass = getInventoryStockProgressFillClassForItem(stockContext);

  const memberUid = latestResourceBookingWithInventoryId?.uid || "";
  const { currentMember } = useLoadMember(memberUid);
  const planName = currentMember?.plan_name ? `${currentMember?.plan_name} - $${styleThousandCommas(currentMember?.plan_rate || 0)} / ${currentMember?.plan_recurrence}` : "No plan assigned";
  const planValue = currentMember?.plan_name ? (
    <div className="flex items-center gap-2">
      <span>{planName}</span>
    </div>
  ) : (
    <span className="text-inventory-primary/50 text-xs italic">{planName}</span>
  );

  const bookingTimezone = latestResourceBookingWithInventoryId?.timezone || DEFAULT_TIMEZONE;
  const bookingStartTime = formatSessionTime(latestResourceBookingWithInventoryId?.start_time, bookingTimezone);
  const bookingEndTime = formatSessionTime(latestResourceBookingWithInventoryId?.end_time, bookingTimezone);

  const duration = latestResourceBookingWithInventoryId?.total_hours || 0

  const startTime = moment(latestResourceBookingWithInventoryId?.start_time).tz(bookingTimezone)
  const endTime = moment(latestResourceBookingWithInventoryId?.end_time).tz(bookingTimezone)
  const nowTime = moment().tz(bookingTimezone);

  const timeElapsedPercentage =
    startTime && endTime && nowTime && endTime.isAfter(startTime)
      ? Math.min(
          100,
          Math.max(0, Math.round((nowTime.diff(startTime) / endTime.diff(startTime)) * 100))
        )
      : 0;

  const memberName = currentMember?.first_name && currentMember?.last_name
        ? `${currentMember?.first_name} ${currentMember?.last_name}`
        : "No member assigned"

  const memberValue = (currentMember?.avatar) ? (
      <div className="flex items-center gap-2">
        <UserAvatar
          avatar={currentMember?.avatar}
          firstName={currentMember?.first_name}
          lastName={currentMember?.last_name}
          username={currentMember?.username}
          email={currentMember?.email}
          size="sm"
        />
        <span>{memberName}</span>
      </div>
    ) : (
      <span className="text-inventory-primary/50 text-xs italic">{memberName}</span>
    );

  const stockSummary = `${usedQty} of ${totalQty} Used, ${Math.max(availableQty, 0)} left`;
  const usagePrice = `$${(inventory?.unit_price || 0).toFixed(2)}/${inventory?.measure_unit || "unit"}`;
  const unitPrice = inventory?.unit_price || 0;
  const tracksUsage = Boolean(inventory?.track_usage);

  const usageRows = useMemo(
    () =>
      sortedSessions.filter((booking) =>
        USAGE_STATUSES.includes(String(booking.status || "").toLowerCase() as BookingStatusType)
      ),
    [sortedSessions]
  );

  const usageSummary = useMemo(() => {
    return usageRows.reduce(
      (acc, booking) => {
        const qty = getBookingInventoryQuantity(booking, inventoryId);
        const hours = Number(booking.total_hours) || 0;
        acc.quantity += qty;
        acc.hours += hours;
        acc.cost += tracksUsage ? qty * unitPrice : 0;
        return acc;
      },
      { quantity: 0, hours: 0, cost: 0 }
    );
  }, [usageRows, inventoryId, tracksUsage, unitPrice]);

  const auditEvents = useMemo(() => {
    const events: { id: string; at: string; label: string; detail: string }[] = [];
    const inventoryUpdated = (inventory as InventorySchema & { last_updated?: string } | null)?.last_updated;
    if (inventoryUpdated) {
      events.push({
        id: `inventory-updated-${inventoryId}`,
        at: inventoryUpdated,
        label: "Inventory updated",
        detail: inventory?.item_name || inventoryId,
      });
    }
    matchingBookings.forEach((booking, index) => {
      const party = getBookingPartyName(booking);
      const statusLabel = getSessionStatusLabel(booking.status);
      if (booking.created) {
        events.push({
          id: `${booking.id || index}-created`,
          at: booking.created,
          label: "Booking created",
          detail: `${party} · ${statusLabel}`,
        });
      }
      if (booking.last_updated && booking.last_updated !== booking.created) {
        events.push({
          id: `${booking.id || index}-updated`,
          at: booking.last_updated,
          label: "Booking updated",
          detail: `${party} · ${statusLabel}`,
        });
      } else if (!booking.created && booking.start_time) {
        events.push({
          id: `${booking.id || index}-session`,
          at: booking.start_time,
          label: `Booking ${statusLabel.toLowerCase()}`,
          detail: party,
        });
      }
    });
    return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [matchingBookings, inventory, inventoryId]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="flex gap-1 overflow-x-auto border-b border-inventory-border px-3 py-2"
        data-testid="inventory-details-tabs"
      >
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setTab(entry.id)}
            className={cn(
              "shrink-0 rounded-lg px-3 py-2 font-raleway text-xs font-bold uppercase tracking-wide",
              tab === entry.id
                ? "bg-inventory-primary text-white"
                : "text-inventory-primary/45 hover:bg-inventory-surface-muted"
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {tab === "overview" && (
          <div className="space-y-0">
            {inventory?.item_description?.trim() && (
              <div className="border-b border-inventory-border pb-3" data-testid="inventory-item-description">
                <p className="font-open-sans text-sm text-inventory-primary/50">Description</p>
                <p className="mt-0.5 whitespace-pre-wrap font-open-sans text-sm text-inventory-primary">
                  {inventory.item_description.trim()}
                </p>
              </div>
            )}

            <OverviewRow
              icon={Clock}
              label="Current Time"
              value={nowTime?.format("ddd MMM DD, h:mm A") || "N/A"}
            />
            <OverviewRow icon={List} label="S/N" value={inventory?.id || "N/A"} />
            <OverviewRow
              icon={FileText}
              label="Stock Details"
              value={stockSummary}
              progress={usedPct}
              progressTrackClass={inventoryStockStatusDotClass.available}
              progressFillClass={usedPct > 0 ? stockProgressFillClass : undefined}
            />
            <OverviewRow icon={DollarSign} label="Plan" value={planValue} />
            <OverviewRow
              icon={RefreshCw}
              label="Status"
              value={stockStatusLabel}
              statusDot={stockStatusDot}
            />
            <OverviewRow icon={User} label="Member" value={memberValue} />
            <OverviewRow
              icon={Clock}
              label="Duration"
              value={duration}
              progress={timeElapsedPercentage}
              progressTone="muted"
            />
            <OverviewRow icon={Calendar} label="Start Time" value={bookingStartTime} />
            <OverviewRow icon={Calendar} label="End Time" value={bookingEndTime} />
            <OverviewRow icon={DollarSign} label="Usage Price" value={usagePrice} />
          </div>
        )}

        {tab === "sessions" && (
          sortedSessions.length === 0 ? (
            <EmptyTabPanel message="No booking sessions recorded for this item yet." />
          ) : (
            <ul className="space-y-3" data-testid="inventory-sessions-list">
              {sortedSessions.map((booking, index) => {
                const qty = getBookingInventoryQuantity(booking, inventoryId);
                const tz = booking.timezone || DEFAULT_TIMEZONE;
                return (
                  <li
                    key={booking.id || `${booking.start_time}-${index}`}
                    className="rounded-xl border border-inventory-border bg-white px-4 py-3 cursor-pointer hover:bg-inventory-surface-muted/40 transition-colors"
                    data-testid="inventory-session-row"
                    onClick={() => handleOpenRequestDetails(booking)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-raleway text-sm font-semibold text-inventory-primary">
                        {getBookingPartyName(booking)}
                      </p>
                      <SessionStatusChip status={booking.status} />
                    </div>
                    <p className="mt-1 font-open-sans text-xs text-inventory-primary/60">
                      {formatSessionTime(booking.start_time, tz)} – {formatSessionTime(booking.end_time, tz)}
                    </p>
                    <p className="mt-1 font-open-sans text-xs text-inventory-primary/70">
                      {qty} {inventory?.measure_unit || "unit"}
                      {booking.total_hours ? ` · ${booking.total_hours} hr` : ""}
                    </p>
                  </li>
                );
              })}
            </ul>
          )
        )}

        {tab === "usage" && (
          usageRows.length === 0 ? (
            <EmptyTabPanel message="No admin usage log entries recorded for this item yet." />
          ) : (
            <div data-testid="inventory-usage-list">
              <div className="mb-3 rounded-xl border border-inventory-border bg-inventory-surface-muted/30 px-4 py-3">
                <p className="font-raleway text-xs font-bold uppercase tracking-wide text-inventory-primary/50">
                  Usage summary
                </p>
                <p className="mt-1 font-open-sans text-sm text-inventory-primary">
                  {usageSummary.quantity} {inventory?.measure_unit || "unit"} · {usageSummary.hours} hr
                  {tracksUsage ? ` · $${usageSummary.cost.toFixed(2)}` : ""}
                </p>
              </div>
              <ul className="space-y-3">
                {usageRows.map((booking, index) => {
                  const qty = getBookingInventoryQuantity(booking, inventoryId);
                  const cost = tracksUsage ? qty * unitPrice : 0;
                  const tz = booking.timezone || DEFAULT_TIMEZONE;
                  return (
                    <li
                      key={booking.id || `${booking.start_time}-usage-${index}`}
                      className="rounded-xl border border-inventory-border bg-white px-4 py-3"
                      data-testid="inventory-usage-row"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-raleway text-sm font-semibold text-inventory-primary">
                          {getBookingPartyName(booking)}
                        </p>
                        <SessionStatusChip status={booking.status} />
                      </div>
                      <p className="mt-1 font-open-sans text-xs text-inventory-primary/60">
                        {formatSessionTime(booking.start_time, tz)} – {formatSessionTime(booking.end_time, tz)}
                      </p>
                      <p className="mt-1 font-open-sans text-xs text-inventory-primary/70">
                        {qty} {inventory?.measure_unit || "unit"}
                        {booking.total_hours ? ` · ${booking.total_hours} hr` : ""}
                        {tracksUsage ? ` · $${cost.toFixed(2)}` : ""}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>
          )
        )}

        {tab === "audit" && (
          auditEvents.length === 0 ? (
            <EmptyTabPanel message="No audit entries recorded for this resource yet." />
          ) : (
            <ul className="space-y-3" data-testid="inventory-audit-list">
              {auditEvents.map((event) => (
                <li
                  key={event.id}
                  className="rounded-xl border border-inventory-border bg-white px-4 py-3"
                  data-testid="inventory-audit-row"
                >
                  <p className="font-raleway text-sm font-semibold text-inventory-primary">{event.label}</p>
                  <p className="mt-0.5 font-open-sans text-xs text-inventory-primary/70">{event.detail}</p>
                  <p className="mt-1 font-open-sans text-[11px] text-inventory-primary/50">
                    {formatSessionTime(event.at, DEFAULT_TIMEZONE)}
                  </p>
                </li>
              ))}
            </ul>
          )
        )}
      </div>

      {selectedRequest?.id && viewRequestDetails && ReactDOM.createPortal(
        <RequestDetailsModal
          isOpen={viewRequestDetails}
          closeAppModal={() => {
            toggleViewRequestDetails(false);
            setSelectedRequest(null);
          }}
          allResourceRequests={sortedSessions}
          request={selectedRequest}
        />,
        document.body
      )}
    </div>
  );
};

export default InventoryItemDetails;
